import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("h1", { class: "text-3xl mt-14" }, "DNS propagation checker", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, "Check the DNS propagation for any website using this online tool. This checker supports A, AAAA, NS, MX and TXT records.", -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("p", null, "It helps you debug DNS resolution issues, and to find the time it will take before your DNS records will propagate. It will also show you a warning when your DNS records are misconfigured.", -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("h2", null, "How to check DNS records?", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", null, "Not sure how to check DNS records? There are multiple ways, and we'll go through all of them:", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("ol", null, [
  /*#__PURE__*/_createElementVNode("li", null, "From the command line on Linux or Mac;"),
  /*#__PURE__*/_createElementVNode("li", null, "From the command line on Windows;"),
  /*#__PURE__*/_createElementVNode("li", null, "Using an online DNS checker.")
], -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("h3", null, "Command line", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("img", {
  loading: "lazy",
  class: "block my-2 w-full border border-black/10 rounded-md shadow py-2 pr-1 bg-black",
  src: "/img/pages/dns-checker/mac-dig-output.png",
  alt: "Output of the dig command on mac",
  title: "Output of the dig command on mac"
}, null, -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("If you're already familiar with using the command line, you can use a DNS checker that's built into your operating system. Both the "),
  /*#__PURE__*/_createElementVNode("code", null, "dig"),
  /*#__PURE__*/_createTextVNode(" and "),
  /*#__PURE__*/_createElementVNode("code", null, "nslookup"),
  /*#__PURE__*/_createTextVNode(" DNS checkers help you to quickly find the DNS records for a hostname.")
], -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("code", null, "dig"),
  /*#__PURE__*/_createTextVNode(" comes pre-installed on Linux and MacOS. To check DNS records for a particular website, open a terminal and type "),
  /*#__PURE__*/_createElementVNode("code", null, "dig google.com"),
  /*#__PURE__*/_createTextVNode(", then hit enter. This will show the A records of the domain name google.com. To check a different record type, simply append it to the command like so: "),
  /*#__PURE__*/_createElementVNode("code", null, "dig google.com MX"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("You can also use dig on Windows, but you'll have to install it first. It's more convenient to use the built-in nslookup command if you're using Windows. Open a terminal (Start → cmd.exe) and type "),
  /*#__PURE__*/_createElementVNode("code", null, "nslookup facebook.com"),
  /*#__PURE__*/_createTextVNode(" to find the IP addresses that host Facebook.com. If you want to check for different record types than A records, you can add the "),
  /*#__PURE__*/_createElementVNode("code", null, "type"),
  /*#__PURE__*/_createTextVNode(" argument. So, "),
  /*#__PURE__*/_createElementVNode("code", null, "nslookup -type=TXT facebook.com"),
  /*#__PURE__*/_createTextVNode(" retrieves all TXT records of Facebook.com.")
], -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", null, "If you want to check the propagation of the DNS records at servers around the globe, then you'll have to execute this command for each server. This is where the online DNS checker comes in handy.", -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("h3", null, "Online tool", -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("img", {
  loading: "lazy",
  src: "/img/pages/dns-checker/nslookup-output.png",
  alt: "Output of an online DNS checker",
  title: "Output of an online DNS checker"
}, null, -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("p", null, "A more convenient way to find DNS records is to use an online DNS checker like the one on this page. It helps you check DNS records right from your browser, without the need for a command line. Simply scroll up, type the name of the website you want to check the DNS records of, and click \"check propagation\".", -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("h2", null, "DNS domain tester", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("p", null, "The Domain Name System (DNS) contains every domain name. It's sometimes useful to test a DNS domain to see what the IP addresses it resolves to. You can check a DNS domain with the tool on this page.", -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("p", null, "Scroll to the DNS domain checker at the top of this page and type the domain name you want to test. Click \"check propagation\" to see all DNS records for that domain.", -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("h2", null, "Why check DNS propagation?", -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("p", null, "Whether you're setting up a new website, updating records for an existing site, or checking the configuration of a mail server, a DNS propagation checker can come in handy!", -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("h3", null, "New website", -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("p", null, "When setting up a new website, one of the things you'll have to do is configure DNS records. Nearly all hosting providers have dedicated DNS servers. You can use these to configure DNS records. If you want to use DNS servers of another organization, you'll have to point the NS records from the hosting provider there. This process can take a while, so it can be convenient to check if the DNS records are still propagating, or have already fully propagated.", -1)
const _hoisted_23 = /*#__PURE__*/_createElementVNode("h3", null, "Check updated records", -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("p", null, "If you're changing DNS records, there might be some DNS servers that cache the old version of your records. They will eventually remove that version, but it might take a while before the so-called 'time to live' (TTL) expires. So, if you've just updated your DNS records, you can use a DNS checker to find out whether the DNS servers are serving a fresh or stale set of records.", -1)
const _hoisted_25 = /*#__PURE__*/_createElementVNode("h3", null, "Mail server configuration", -1)
const _hoisted_26 = /*#__PURE__*/_createElementVNode("p", null, "Email works like a distributed system, consisting of independent mail servers. The mail servers from Gmail and Outlook.com, for example, can change independently. They need some way to communicate these changes. That's where DNS comes in.", -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("p", null, "Everyone that has configured email for a domain name, has to update their DNS records. MX records tell which to which servers an email for a certain domain should be sent. TXT records contain SPF data that prevents spam. You can inspect these settings with a DNS checker.", -1)
const _hoisted_28 = /*#__PURE__*/_createElementVNode("h2", null, "How does it work?", -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("p", null, "You can check the DNS propagation of a domain name like this:", -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("ol", null, [
  /*#__PURE__*/_createElementVNode("li", null, "Fill in a domain name in on the top of this page and click \"check propagation\"."),
  /*#__PURE__*/_createElementVNode("li", null, "One of the servers of Nslookup will query the DNS records for that website using many local and global DNS servers."),
  /*#__PURE__*/_createElementVNode("li", null, "The DNS servers responds with DNS records."),
  /*#__PURE__*/_createElementVNode("li", null, "The DNS records are parsed and grouped, so you can easily see which servers respond with the same set of DNS records.")
], -1)

import DnsRecordsLink from '@/components/micro/DnsRecordsLink.vue';
import MetaTags from '@/components/MetaTags.vue';
import SearchWithHiddenContentPage from '@/components/pages/SearchWithHiddenContentPage.vue';

export default /*#__PURE__*/_defineComponent({
  __name: 'DnsChecker',
  setup(__props) {


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      _createVNode(MetaTags, {
        title: "DNS Propagation Checker",
        description: "Check global DNS propagation for any website using this free online tool. Global DNS propagation checker — check DNS records around the world.",
        url: "https://www.nslookup.io/dns-checker/"
      })
    ])),
    _createVNode(SearchWithHiddenContentPage, {
      "domain-search-to-url": (encodedDomain) => `/domains/${encodedDomain}/dns-propagation/a/`,
      "search-button-text": "Check propagation"
    }, {
      "search-explanation": _withCtx(() => [
        _createTextVNode(" Check the DNS propagation for any website using this online tool. For example, try "),
        _createVNode(DnsRecordsLink, {
          page: "propagation",
          domain: "youtube.com",
          class: "text-blue-600 hover:text-blue-800 hover:underline"
        }),
        _createTextVNode(" or "),
        _createVNode(DnsRecordsLink, {
          page: "propagation",
          domain: "amazon.com",
          class: "text-blue-600 hover:text-blue-800 hover:underline"
        }),
        _createTextVNode(" to view their DNS propagation. ")
      ]),
      content: _withCtx(() => [
        _hoisted_1,
        _hoisted_2,
        _hoisted_3,
        _hoisted_4,
        _hoisted_5,
        _hoisted_6,
        _hoisted_7,
        _hoisted_8,
        _hoisted_9,
        _hoisted_10,
        _hoisted_11,
        _hoisted_12,
        _hoisted_13,
        _hoisted_14,
        _hoisted_15,
        _hoisted_16,
        _hoisted_17,
        _hoisted_18,
        _hoisted_19,
        _hoisted_20,
        _hoisted_21,
        _hoisted_22,
        _hoisted_23,
        _hoisted_24,
        _hoisted_25,
        _hoisted_26,
        _hoisted_27,
        _hoisted_28,
        _hoisted_29,
        _hoisted_30
      ]),
      _: 1
    }, 8, ["domain-search-to-url"])
  ], 64))
}
}

})